import { useState, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { ModalContext, ModalKey } from 'components/Modals/ModalContext'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { WhichWeightAPI } from 'WhichWeightAPI'
import { CheckIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { GetCoachInvitePlainText } from 'functions/textUtils'

import ScreenFrame from 'components/Frame/ScreenFrame'
import SolidButton from 'components/Buttons/SolidButton'
import BrandColorButton from 'components/Buttons/BrandColorButton'

export default function CoachInviteView() {

    const defaultCoachEmailObject = {
        email: '',
        validationError: ''
    }

    const navigate = useNavigate()

    const { ShowModal } = useContext(ModalContext)

    const userId = useSelector((state) => state.user.id)
    const organizationId = useSelector((state) => state.user.organizationId)
    const organizationName = useSelector((state) => state.user.organizationName)
    const organizationCode = useSelector((state) => state.user.organizationCode)

    const [coachEmails, setCoachEmails] = useState([JSON.parse(JSON.stringify(defaultCoachEmailObject))])
    const [invitesLoading, setInvitesLoading] = useState(false)

    const ShowCoachInviteImportModal = () => {
        const modalData = {
            key: ModalKey.FileImport,
            size: 'third',
            titleText: 'Upload a CSV file of coach emails',
            fileFormatDescriptionText: 'The CSV file should contain a list of emails each separated by a newline',
            exampleFileObject: (
                <div className='w-full bg-gray-100 pt-5 px-4 pb-4 rounded-md relative'>
                    <p>coach1@gmail.com</p>
                    <p>coach2@gmail.com</p>
                    <p>coach3@gmail.com</p>

                    <div className='absolute top-0 left-0 text-xs text-gray-400 font-semibold py-1 px-2'>example_coaches.csv</div>
                </div>
            ),
            buttonText: 'Import Coaches',
            buttonAction: (fileData) => ProcessCSVFile(fileData)
        }

        ShowModal(modalData)
    }

    const ProcessCSVFile = (fileData) => {

        const reader = new FileReader()
        
        reader.onload = (e) => {

            const text = e.target.result
            const normalizedText = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n') // Normalize line endings (handle Windows \r\n, Mac \r, and Unix \n)
            
            // console.log(normalizedText)

            // Split by newline and filter out empty lines
            const csvFileDataArray = normalizedText.split('\n').map(line => line.trim()).filter(line => line.length > 0)
            
            // console.log(csvFileDataArray)
            
            setCoachEmails(csvFileDataArray.map(email => ({
                email: email,
                validationError: ''
            })))
        }
        
        reader.readAsText(fileData)

    }

    const HandleCoachEmailChange = (index, email) => {
        var targetEmailObject = coachEmails[index]

        targetEmailObject.email = email
        targetEmailObject.validationError = ''

        setCoachEmails(coachEmails.map((e, i) => 
            i === index 
                ? targetEmailObject
                : e
        ))
    }

    const HandleRemoveCoachEmail = (index) => {
        if (coachEmails.length <= 1) return
        setCoachEmails(coachEmails.filter((_, i) => i !== index))
    }

    const ClearAllEmailErrors = () => {
        setCoachEmails(coachEmails.map(emailObj => ({
            ...emailObj,
            validationError: ''
        })))
    }

    const ValidateEmails = () => {

        var allEmailsValid = true
        
        var coachEmailsWithValidationErrors = coachEmails.map(emailObj => {
            const trimmedEmail = emailObj.email.trim()
            
            if (
                trimmedEmail === ''
                || !IsValidEmailRegex(trimmedEmail)
            ) {
                allEmailsValid = false

                return {
                    ...emailObj,
                    validationError: 'Invalid email address'
                }
            }
            
            return emailObj
        })
        
        setCoachEmails(coachEmailsWithValidationErrors)

        return allEmailsValid
    }

    const IsValidEmailRegex = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return emailRegex.test(email)
    }

    const OnSendInvites = async () => {

        ClearAllEmailErrors()

        if (coachEmails.length === 0) {
            return
        }

        if (ValidateEmails() === false) {
            return
        }

        setInvitesLoading(true)

        try {
            const jsonBody = {
                coachId: userId,
                organizationId: organizationId,
                organizationName: organizationName,
                organizationCode: organizationCode,
                recipients: coachEmails.map(emailObj => emailObj.email)
            }

            // console.log(jsonBody)

            const result = await WhichWeightAPI('EMAIL', 'POST', '/invite_coaches_to_organization', jsonBody)

            // console.log(result)

            if (result.data.status === "failure") {
                ShowFailureModal()
                setInvitesLoading(false)
                console.error(result)
                return
            }

            ShowSuccessModal()
            setInvitesLoading(false)

        } catch (error) {
            ShowFailureModal()
            setInvitesLoading(false)
            console.error(error)
        }
    }

    const ShowSuccessModal = () => {
        const modalData = {
            key: ModalKey.Action,
            size: 'quarter',
            icon: <CheckIcon className="h-8 w-8 text-white" aria-hidden="true" />,
            titleText: 'Success!',
            bodyText: [
                'Coach invites have successfully been sent'
            ],
            buttonText: 'Return to Home',
            buttonAction: () => { navigate('/home') }
        }

        ShowModal(modalData)
    }

    const ShowFailureModal = () => {
        const modalData = {
            key: ModalKey.Action,
            size: 'quarter',
            icon: <ExclamationTriangleIcon className="h-8 w-8 text-white" aria-hidden="true" />,
            titleText: 'Error!',
            bodyText: [
                'Failed to send coach invites. Please try again.',
                'If the problem persists, please contact support@kinesisintegrated.com'
            ],
            buttonText: 'Close'
        }

        ShowModal(modalData)
    }

    const ShowPlainTextEmailPreviewModal = () => {
        const modalData = {
            key: ModalKey.PlainTextEmailPreview,
            size: 'half',
            titleText: 'Coach Invite Email Preview',
            bodyText: [
                'This is a preview of the plain text email that will be sent to the coaches.',
                'Click anywhere below to copy the text to your clipboard.'
            ],
            plainText: GetCoachInvitePlainText(organizationName, organizationCode),
            includeSendFunctionality: false
        }

        ShowModal(modalData)
    }

    return (
        <ScreenFrame>
            <div className='max-w-4xl mx-auto space-y-4'>
                <div className='space-y-1 pb-2'>
                    <div className='flex items-center gap-x-2'>
                        <div className='space-y-1 flex-grow'>
                            <h1 className="text-2xl font-semibold text-gray-900 tracking-tight pt-2">
                                Enter Coach Emails Below
                            </h1>
                        </div>

                        <div className='w-60'>
                            <SolidButton
                                text="Import Coaches"
                                action={ShowCoachInviteImportModal}
                                className='shadow-md'
                            />
                        </div>
                    </div>
                </div>

                {coachEmails.map((emailObj, index) => (
                    <div 
                        key={index}
                        className='relative'
                    >
                        <div className='flex items-center gap-x-4'>
                            <input
                                type="text"
                                className={`
                                    ${emailObj.validationError.length > 0 ? 'bg-red-50' : 'bg-white'}
                                    flex-1 py-2 px-4 rounded-md border border-gray-200 placeholder-gray-500 shadow-md
                                    focus:border-orange-400 focus:outline-none focus:ring-1 focus:ring-orange-400
                                `}
                                placeholder='Enter Coach Email Here'
                                value={emailObj.email}
                                onChange={(event) => HandleCoachEmailChange(index, event.target.value)}
                            />

                            <XMarkIcon 
                                className='h-5 w-5 text-gray-400 cursor-pointer' 
                                onClick={() => HandleRemoveCoachEmail(index)} 
                            />
                        </div>

                        {emailObj.validationError && (
                            <div className='absolute bottom-3 right-12 pl-2 bg-red-50 text-red-600 text-sm z-10'>
                                {emailObj.validationError}
                            </div>
                        )}
                    </div>
                ))}

                <div>
                    <p className='-mt-1 text-sm text-gray-500'>{coachEmails.length} coach{coachEmails.length === 1 ? '' : 'es'} added</p>
                </div>
                
                <div className='pt-2 grid grid-cols-2 gap-x-4'>
                    <SolidButton
                        text="Add Coach Email"
                        action={() => setCoachEmails([...coachEmails, { ...defaultCoachEmailObject }])}
                        className='shadow-md'
                    />

                    <BrandColorButton 
                        text="Send Invites"
                        action={OnSendInvites}
                        isLoading={invitesLoading}
                        className='shadow-md'
                    />
                </div>

                <p 
                    className='pt-4 text-center text-sm text-blue-500 underline cursor-pointer'
                    onClick={ShowPlainTextEmailPreviewModal}
                >
                    What if I want to send the email myself?
                </p>
            </div>
        </ScreenFrame>
    )
}
